import type { KaiFeedEventContext } from "@/services/kaiApi";
import type { FeedEvent } from "@/types/feed";
import { feedEventDisplayTitle } from "@/features/feed/NewBookingMailFeedDetail";

/**
 * Build the event-level snapshot Kai receives in
 * `client_context.feed_event_context` when the user picks "Ask Kai" on a feed
 * row. Carries the ids and headline facts so Kai can fetch the full event with
 * `api_get_feed_event` and keep the event in scope across follow-up turns —
 * without the user ever pasting an id into the chat.
 */
export function buildKaiFeedEventContext(event: FeedEvent): KaiFeedEventContext {
  const base: KaiFeedEventContext = {
    event_id: event.id,
    correlation_id: event.correlation_id,
    event_type: event.event_type,
    title: feedEventDisplayTitle(event),
    severity: event.severity,
    reference_type: event.reference_type,
    reference_id: event.reference_id,
    children_count: event.children_count,
  };

  // For a classified Outlook mail event, carry the Graph linkage so Kai can open
  // the exact message with get_graph_mail_context instead of re-searching.
  const payload = event.payload as { kind?: string } | null | undefined;
  if (payload?.kind === "graph_mail") {
    const mail = payload as {
      graph_message_id?: string | null;
      mailbox_id?: string | null;
      conversation_id?: string | null;
      booking_source_id?: string | null;
      attachments?: Array<{
        mailbox_id: string;
        message_id: string;
        attachment_id: string;
        filename: string;
      }>;
    };
    base.graph_message_id = mail.graph_message_id ?? null;
    base.mailbox_id = mail.mailbox_id ?? null;
    base.conversation_id = mail.conversation_id ?? null;
    base.booking_source_id = mail.booking_source_id ?? null;
    if (Array.isArray(mail.attachments) && mail.attachments.length > 0) {
      base.graph_attachments = mail.attachments.map((a) => ({
        mailbox_id: a.mailbox_id,
        message_id: a.message_id,
        attachment_id: a.attachment_id,
        filename: a.filename,
      }));
    }
  }

  return base;
}

/**
 * The short, user-visible prompt that seeds the new chat. The ids live in
 * `feed_event_context`, so the trigger stays clean and natural in the thread.
 */
export function buildAskKaiFeedEventPrompt(_event: FeedEvent): string {
  return "Summarize and explain this feed event for me.";
}
